import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "moment";
import { useApi } from "../../utils/api";
import { useAuth } from "../../utils/auth";
import { useUserInfo } from "../../utils/user";

/**
 * Self-service coach account closure. Uses users-service DELETE (same soft-delete
 * as removing a client). Paid coaches: subscription ends at period end, then account closes.
 * Trial / no subscription: immediate closure.
 */
const CoachCloseAccountSection = ({ sub }) => {
  const { id, coachAccountDeletionPending, coachAccountDeletionEffectiveAt } =
    useUserInfo();
  const { deleteUser } = useApi();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasPaidSubscription = !!sub?.hasSubscription;
  const effectiveTs =
    coachAccountDeletionEffectiveAt != null
      ? Number(coachAccountDeletionEffectiveAt)
      : null;

  const handleConfirm = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await deleteUser(id);
      if (res?.error) {
        throw new Error(
          typeof res.error === "string" ? res.error : "Request failed"
        );
      }
      if (res?.scheduled) {
        setOpen(false);
        localStorage.removeItem("userInfo");
        window.location.reload();
        return;
      }
      setOpen(false);
      await signOut();
    } catch (e) {
      setError(e?.message || "Failed to close account");
    } finally {
      setLoading(false);
    }
  };

  if (coachAccountDeletionPending && effectiveTs) {
    return (
      <Alert severity="warning" sx={{ mt: 3 }}>
        Your coach account is scheduled to close on{" "}
        <strong>{moment.unix(effectiveTs).format("MMM D, YYYY")}</strong> when
        your billing period ends. You can keep using Trainer App until then.
      </Alert>
    );
  }

  return (
    <>
      <Card sx={{ mt: 4 }} variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Delete Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account, this cannot be undone.
          </Typography>
          <Button
            color="error"
            variant="outlined"
            onClick={() => {
              setError("");
              setOpen(true);
            }}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => !loading && setOpen(false)}>
        <DialogTitle>Delete account?</DialogTitle>
        <DialogContent>
          {hasPaidSubscription && sub?.currentPeriodEnd ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Your subscription will cancel at the end of your current billing
              period (
              <strong>
                {moment.unix(sub.currentPeriodEnd).format("MMM D, YYYY")}
              </strong>
              ). Your account will be deleted then and you will lose access. You
              can keep using Trainer App until that date.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Your free trial will end immediately. Your account will be deleted
              immediately and you will lose access.
            </Typography>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Back
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            onClick={handleConfirm}
            loading={loading}
          >
            Confirm close account
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoachCloseAccountSection;
